import React, { useState, useEffect } from 'react';
import Modal from '../../../components/ui/Modal';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const DocumentFormModal = ({ isOpen, onClose, onSubmit, documentType }) => {
  const [formData, setFormData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  // Auto-save functionality
  useEffect(() => {
    const autoSaveTimer = setTimeout(() => {
      if (Object.keys(formData)?.length > 0) {
        handleAutoSave();
      }
    }, 3000);

    return () => clearTimeout(autoSaveTimer);
  }, [formData]);

  const handleAutoSave = () => {
    localStorage.setItem(`documentFormDraft_${documentType}`, JSON.stringify(formData));
    setLastSaved(new Date());
  };

  const handleSave = () => {
    setIsSaving(true);
    handleAutoSave();
    setTimeout(() => {
      setIsSaving(false);
      alert('تم حفظ المسودة بنجاح');
    }, 500);
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title="إنشاء مستند" 
      size="large"
      footer={null}
      icon={null}
      iconColor={null}
    >
      <div className="space-y-6">
        {lastSaved && (
          <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 border border-green-200 rounded-lg p-3">
            <Icon name="Check" size={16} color="#16a34a" />
            <span>تم الحفظ التلقائي في {lastSaved?.toLocaleTimeString('ar-JO')}</span>
          </div>
        )}
        <div className="space-y-6">
          <div>
            <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Building2" size={20} />
              معلومات العقار
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="اسم العقار"
                type="text"
                placeholder="أدخل اسم العقار"
                value={formData?.propertyName}
                onChange={(e) => setFormData({ ...formData, propertyName: e?.target?.value })}
                required
              />
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  نوع العقار
                </label>
                <Input
                  type="text"
                  value={formData?.propertyType}
                  onChange={(e) => setFormData({ ...formData, propertyType: e?.target?.value })}
                  placeholder="مثال: شقة، فيلا، مكتب"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  تصنيف العقار
                </label>
                <select
                  value={formData?.realEstateType}
                  onChange={(e) => setFormData({ ...formData, realEstateType: e?.target?.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">اختر التصنيف</option>
                  <option value="سكني">سكني</option>
                  <option value="تجاري">تجاري</option>
                </select>
              </div>
              <Input
                label="الموقع"
                type="text"
                placeholder="أدخل موقع العقار"
                value={formData?.location}
                onChange={(e) => setFormData({ ...formData, location: e?.target?.value })}
                required
              />
              <Input
                label="السعر (دينار أردني)"
                type="number"
                placeholder="أدخل السعر"
                value={formData?.price}
                onChange={(e) => setFormData({ ...formData, price: e?.target?.value })}
                required
              />
            </div>
          </div>

          <div>
            <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="User" size={20} />
              معلومات المالك
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="اسم المالك"
                type="text"
                placeholder="أدخل اسم المالك"
                value={formData?.ownerName}
                onChange={(e) => setFormData({ ...formData, ownerName: e?.target?.value })}
                required
              />
              <Input
                label="رقم الهاتف"
                type="tel"
                placeholder="+971 XX XXX XXXX"
                value={formData?.ownerPhone}
                onChange={(e) => setFormData({ ...formData, ownerPhone: e?.target?.value })}
                required
              />
              <Input
                label="البريد الإلكتروني"
                type="email"
                placeholder="owner@example.com"
                value={formData?.ownerEmail}
                onChange={(e) => setFormData({ ...formData, ownerEmail: e?.target?.value })}
                required
              />
              <Input
                label="رقم الهوية"
                type="text"
                placeholder="أدخل رقم الهوية"
                value={formData?.ownerId}
                onChange={(e) => setFormData({ ...formData, ownerId: e?.target?.value })}
                required
              />
            </div>
          </div>

          <div>
            <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="FileText" size={20} />
              تفاصيل إضافية
            </h3>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                تفاصيل إضافية
              </label>
              <textarea
                value={formData?.additionalDetails}
                onChange={(e) => setFormData({ ...formData, additionalDetails: e?.target?.value })}
                placeholder="أدخل أي تفاصيل إضافية عن العقار"
                rows={4}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                ملاحظات
              </label>
              <textarea
                value={formData?.remarks}
                onChange={(e) => setFormData({ ...formData, remarks: e?.target?.value })}
                placeholder="أدخل أي ملاحظات أو تعليقات إضافية"
                rows={3}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 pt-4 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={handleSave}
            disabled={isSaving}
            iconName="Save"
            iconSize={18}
          >
            {isSaving ? 'جاري الحفظ...' : 'حفظ المسودة'}
          </Button>
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={onClose}>
              إلغاء
            </Button>
            <Button type="button" variant="primary" onClick={() => onSubmit(formData)}>
              إنشاء المستند
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default DocumentFormModal;